/**
 * Per-session sync access gate (RFC 0233).
 *
 * In Mod the session log is the unit of access, so "who may sync this session"
 * is derived from the session's OWN log, not a branch ACL. This module is the
 * pure decision function a sync server calls to decide whether to admit a
 * session doc to a connecting peer.
 *
 * Security properties (why this is safe, not circular or forgeable):
 *   - NOT circular: the SERVER already holds the session doc, so projecting its
 *     participants to gate a PEER needs nothing the peer must first be given.
 *   - NOT forgeable: a peer is admitted only if it is a participant, and a
 *     participant entry can only be appended by an
 *     existing participant — so a peer cannot write itself in. The creator is
 *     the root of trust, supplied out-of-band by the embedder (e.g. the session
 *     doc's `createdBy`), never read from the mutable log.
 *   - Private by default: a fresh session (no participant entries) admits only its
 *     creator.
 *
 * This is a pure function over the projected log; the sync-server wiring (read
 * the creator from the session doc, call this per connection) is the embedder's
 * job and is spiked separately before it lands.
 */

import { sessionParticipants } from './projection-participants'
import type { LogDoc } from './types'

export interface SessionAccessOptions {
  /**
   * The session creator's member id, supplied out-of-band (e.g. the session
   * doc's `createdBy`). The creator is always a participant and is the root of
   * trust — it is NOT taken from the log, so it cannot be forged by a peer.
   */
  creator?: string
}

/**
 * The full set of member ids allowed to sync this session: everyone the log
 * names as a participant, plus the
 * out-of-band creator. Private by default — with no participant entries and no creator,
 * the set is empty.
 */
export function sessionAccessSet(
  doc: LogDoc,
  opts: SessionAccessOptions = {},
): Set<string> {
  const set = sessionParticipants(doc)
  if (opts.creator) set.add(opts.creator)
  return set
}

/**
 * The gate: may `peerMemberId` sync this session? True iff the peer is a
 * participant of the session log or its creator. This is the predicate a sync
 * server evaluates per (session, peer) at connect time.
 */
export function peerMaySyncSession(
  doc: LogDoc,
  peerMemberId: string,
  opts: SessionAccessOptions = {},
): boolean {
  if (!peerMemberId) return false
  if (opts.creator && peerMemberId === opts.creator) return true
  return sessionParticipants(doc).has(peerMemberId)
}
